import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { StaticDatePicker } from '@mui/x-date-pickers';

import Icon from '@sopranodesign/pulse-icons';

import {
  Input,
  Label,
  Airplane,
  Container,
  LeftSection,
  ComposerButton,
  labelAnimations
} from '../Custom/MessageComposer';
import { useComposer, useDelivery } from '../../../../../hooks';
import { DateProvider } from '../../../../../Providers';
import { DateWrapper } from '../../../../../Components';

const isValid = date => dayjs(date, 'DD-MM-YYYY').isValid();

const CustomCalendarHeaderRoot = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 16px',
  alignItems: 'center',
};

function CustomCalendarHeader({ date, onMonthChange, onYearClick }) {
  const selectNextMonth = () => onMonthChange(dayjs(date).add(1, 'month'));
  const selectPreviousMonth = () => onMonthChange(dayjs(date).subtract(1, 'month'));

  return (
    <div style={CustomCalendarHeaderRoot}>
      <Stack spacing={1} direction="row">
        <Icon
          type="cheveronLeft"
          width="1.25rem"
          height="1.25rem"
          onClick={selectPreviousMonth}
          title="Previous month"
        />
      </Stack>
      <Typography
        variant="body2"
        style={{ cursor: 'pointer' }}
        onClick={onYearClick}
      >
        {dayjs(date).format('MMMM YYYY')}
      </Typography>
      <Stack spacing={1} direction="row">
        <Icon
          type="cheveronRight"
          width="1.25rem"
          height="1.25rem"
          onClick={selectNextMonth}
          title="Next month"
        />
      </Stack>
    </div>
  );
}

CustomCalendarHeader.propTypes = {
  date: PropTypes.instanceOf(dayjs).isRequired,
  onMonthChange: PropTypes.func.isRequired,
  onYearClick: PropTypes.func.isRequired,
};

const DateComposer = () => {
  const [open, setOpen] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { t } = useTranslation('full_page');
  const { reply, setReply } = useComposer();
  const { sendMessage } = useDelivery();

  const handleChange = date => {
    setReply(dayjs(date).format('DD-MM-YYYY'));

    if (showYearPicker) {
      setShowYearPicker(false);
    }
  };

  const handleInputClick = () => {
    if (!initialized && !reply) {
      setReply(dayjs().format('DD-MM-YYYY'));
      setInitialized(true);
    }
    setOpen(prev => !prev);
  };

  const handleSend = () => {
    if (isValid(reply)) {
      sendMessage(reply);
      setOpen(false);
      setReply('');
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const value = reply ? dayjs(reply, 'DD-MM-YYYY') : dayjs();

  return (
    <DateProvider primary="#FFFFFF">
      <DateWrapper>
        {open && (
          <StaticDatePicker
            open
            localeText={{
              datePickerToolbarTitle: dayjs(value).format('YYYY'),
            }}
            margin="dense"
            maxDate={dayjs().add(10, 'year')}
            onAccept={date => {
              handleChange(date);
              setOpen(false);
            }}
            onChange={handleChange}
            value={value}
            views={['year', 'month', 'day']}
            openTo={showYearPicker ? 'year' : 'day'}
            slots={{
              calendarHeader: CustomCalendarHeader,
            }}
            slotProps={{
              calendarHeader: {
                date: value,
                onYearClick: () => setShowYearPicker(prev => !prev),
                onMonthChange: newDate => handleChange(newDate),
              },
              toolbar: {
                onClick: () => setShowYearPicker(prev => !prev),
                sx: { cursor: 'pointer' },
              },
              actionBar: { actions: [] },
            }}
            variant="dialog"
          />
        )}
        <Container>
          <LeftSection htmlFor="date-composer">
            <Label
              htmlFor="date-composer"
              initial="expanded"
              variants={labelAnimations}
              animate={open || reply ? 'minimised' : 'expanded'}
            >
              {t('date_composer_label')}
            </Label>
            <Input
              id="date-composer"
              readOnly
              value={reply}
              onClick={handleInputClick}
              onKeyDown={handleKeyDown}
            />
          </LeftSection>
          <ComposerButton
            aria-label={t('send_button_label')}
            onClick={handleSend}
          >
            <Airplane />
          </ComposerButton>
        </Container>
      </DateWrapper>
    </DateProvider>
  );
};

export default DateComposer;